/**
 * The shared filter bar's state lives in the URL — no store, and every view is
 * a link you can paste to a colleague. Pure functions so the encoding is
 * testable without a router.
 *
 * Custom fields ride along as `meta.<key>=<value>`, exactly the shape the API
 * expects; the set of keys is discovered at runtime from /v1/meta/keys, so
 * nothing here needs to know what they are.
 */

export const RANGE_PRESETS = {
  "1h": { label: "최근 1시간", hours: 1 },
  "24h": { label: "최근 24시간", hours: 24 },
  "7d": { label: "최근 7일", hours: 24 * 7 },
  "30d": { label: "최근 30일", hours: 24 * 30 },
} as const;

export type RangePreset = keyof typeof RANGE_PRESETS;

export const DEFAULT_RANGE: RangePreset = "24h";

export const MULTI_KEYS = ["service_id", "model", "provider", "env", "operation"] as const;
export type MultiKey = (typeof MULTI_KEYS)[number];

export const META_PREFIX = "meta.";

export interface Filters {
  range: RangePreset | "custom";
  /** Only meaningful when range === "custom". ISO strings. */
  from?: string;
  to?: string;
  service_id: string[];
  model: string[];
  provider: string[];
  env: string[];
  operation: string[];
  status?: string;
  q?: string;
  /** Arbitrary custom fields: { feature: "summarize" } */
  meta: Record<string, string>;
}

export function emptyFilters(): Filters {
  return { range: DEFAULT_RANGE, service_id: [], model: [], provider: [], env: [], operation: [], meta: {} };
}

function isPreset(value: string | null): value is RangePreset {
  return value !== null && value in RANGE_PRESETS;
}

export function parseFilters(params: URLSearchParams): Filters {
  const filters = emptyFilters();

  const range = params.get("range");
  if (isPreset(range)) {
    filters.range = range;
  } else if (range === "custom" && params.get("from") && params.get("to")) {
    filters.range = "custom";
    filters.from = params.get("from") ?? undefined;
    filters.to = params.get("to") ?? undefined;
  }

  for (const key of MULTI_KEYS) {
    filters[key] = params.getAll(key).filter(Boolean);
  }
  filters.status = params.get("status") || undefined;
  filters.q = params.get("q") || undefined;

  for (const [key, value] of params.entries()) {
    if (key.startsWith(META_PREFIX) && key.length > META_PREFIX.length && value) {
      filters.meta[key.slice(META_PREFIX.length)] = value;
    }
  }

  return filters;
}

/** Filters -> URL params. Empty values are omitted so links stay short. */
export function serializeFilters(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  params.set("range", filters.range);
  if (filters.range === "custom" && filters.from && filters.to) {
    params.set("from", filters.from);
    params.set("to", filters.to);
  }
  for (const key of MULTI_KEYS) {
    for (const value of filters[key]) {
      params.append(key, value);
    }
  }
  if (filters.status) params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  for (const [key, value] of Object.entries(filters.meta)) {
    if (value) params.set(`${META_PREFIX}${key}`, value);
  }
  return params;
}

/** Absolute [from, to) for the selected range. `now` is injectable for tests. */
export function resolveRange(filters: Filters, now: Date = new Date()): { from: string; to: string } {
  if (filters.range === "custom" && filters.from && filters.to) {
    return { from: filters.from, to: filters.to };
  }
  const preset = RANGE_PRESETS[filters.range as RangePreset] ?? RANGE_PRESETS[DEFAULT_RANGE];
  const to = now;
  const from = new Date(to.getTime() - preset.hours * 3600_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Hourly buckets get unreadable past a few days — switch to daily. */
export function defaultInterval(filters: Filters, now?: Date): "hour" | "day" {
  const { from, to } = resolveRange(filters, now);
  const hours = (Date.parse(to) - Date.parse(from)) / 3600_000;
  return hours > 24 * 3 ? "day" : "hour";
}

/** Filters -> the query params the API reads. */
export function toApiParams(filters: Filters, now?: Date): URLSearchParams {
  const params = new URLSearchParams();
  const { from, to } = resolveRange(filters, now);
  params.set("from", from);
  params.set("to", to);

  for (const value of filters.service_id) params.append("service_id", value);
  for (const value of filters.model) params.append("model", value);
  for (const value of filters.provider) params.append("provider", value);
  for (const value of filters.env) params.append("env", value);
  for (const value of filters.operation) params.append("operation", value);
  if (filters.status) params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  for (const [key, value] of Object.entries(filters.meta)) {
    if (value) params.set(`${META_PREFIX}${key}`, value);
  }
  return params;
}

/** Stable cache key for TanStack Query. */
export function filtersKey(filters: Filters): string {
  const params = serializeFilters(filters);
  params.sort();
  return params.toString();
}

export function activeFilterCount(filters: Filters): number {
  return (
    MULTI_KEYS.reduce((total, key) => total + filters[key].length, 0) +
    (filters.status ? 1 : 0) +
    (filters.q ? 1 : 0) +
    Object.values(filters.meta).filter(Boolean).length
  );
}

export function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}
